import type {
  AllConditions,
  AnyConditions,
  ConditionProperties,
  NestedCondition,
} from 'json-rules-engine';
import type { SelectOption } from '@bbkAdminComponents/select/select';
import { NullishOperator } from '@bbkAdminComponents/experiences/audiences/edit-rule/condition-item';
import { AppFieldType } from '@bbkAdminRedux/data-management/reducers';
import { type EnumLike } from '@bbkAdminUtils/utility-types';

export type RuleTopLevelCondition = AllConditions | AnyConditions;

export enum GroupType {
  AND = 'AND',
  OR = 'OR',
}

export enum GroupOperator {
  all = 'all',
  any = 'any',
}

export function isCondition(
  condition: NestedCondition
): condition is ConditionProperties {
  return (condition as ConditionProperties).fact !== undefined;
}

export function isAndGroup(
  condition: RuleTopLevelCondition
): condition is AllConditions {
  return (condition as AllConditions).all !== undefined;
}

export function isOrGroup(
  condition: RuleTopLevelCondition
): condition is AnyConditions {
  return (condition as AnyConditions).any !== undefined;
}

export function isGroup(
  condition: NestedCondition
): condition is RuleTopLevelCondition {
  return (
    isAndGroup(condition as RuleTopLevelCondition) ||
    isOrGroup(condition as RuleTopLevelCondition)
  );
}

export function getGroupType(
  condition: AllConditions | AnyConditions
): GroupType {
  return isAndGroup(condition) ? GroupType.AND : GroupType.OR;
}

export function getGroupOperator(
  condition: AllConditions | AnyConditions
): GroupOperator {
  return isAndGroup(condition) ? GroupOperator.all : GroupOperator.any;
}

export function getGroupConditions(
  group: AllConditions | AnyConditions
): ConditionProperties[] {
  return ((group as AllConditions).all ??
    (group as AnyConditions).any) as ConditionProperties[];
}

const NullishOperators: Array<SelectOption<string>> = [
  {
    label: 'Is empty',
    value: NullishOperator.isNilOrEmpty,
  },
  {
    label: 'Is not empty',
    value: NullishOperator.isNotNilOrEmpty,
  },
];
const TextOperators: Array<SelectOption<string>> = [
  {
    label: 'Is',
    value: '==',
  },
  {
    label: 'Is not',
    value: '!=',
  },
  {
    label: 'Is one of',
    value: 'String.in',
  },
  {
    label: 'Is not one of',
    value: 'String.notIn',
  },
  {
    label: 'Contains',
    value: 'String.includes',
  },
  {
    label: 'Starts With',
    value: 'String.startsWith',
  },
  {
    label: 'Does not contain',
    value: 'String.notIncludes',
  },
  ...NullishOperators,
];
const NumberOperators: Array<SelectOption<string>> = [
  {
    label: 'Is',
    value: '==',
  },
  {
    label: 'Is not',
    value: '!=',
  },
  {
    label: 'Is less than',
    value: '<',
  },
  {
    label: 'Is less than or equal to',
    value: '<=',
  },
  {
    label: 'Is greater than',
    value: '>',
  },
  {
    label: 'Is greater than or equal to',
    value: '>=',
  },
  ...NullishOperators,
];
const DateOperators: Array<SelectOption<string>> = [
  {
    label: 'Is on the day of',
    value: '==',
  },
  {
    label: 'Is not on the day of',
    value: '!=',
  },
  {
    label: 'Is after the day of',
    value: '>',
  },
  {
    label: 'Is on or after the day of',
    value: '>=',
  },
  {
    label: 'Is before the day of',
    value: '<',
  },
  {
    label: 'Is on or before the day of',
    value: '<=',
  },
  {
    label: 'Happened before',
    value: 'Date.isBefore',
  },
  {
    label: 'Will take place after',
    value: 'Date.isAfter',
  },
  {
    label: 'Will take place within',
    value: 'Date.willHappenWithinDays',
  },
  {
    label: 'Happened within',
    value: 'Date.didHappenWithinDays',
  },
  ...NullishOperators,
];
const BooleanOperators: Array<SelectOption<string>> = [
  {
    label: 'Is',
    value: '==',
  },
];

export const getOperatorsForType = (type?: EnumLike<typeof AppFieldType>) => {
  switch (type) {
    case AppFieldType.decimal:
    case AppFieldType.integer:
      return NumberOperators;
    case AppFieldType.boolean:
      return BooleanOperators;
    case AppFieldType.date:
      return DateOperators;
    case AppFieldType.text:
    case AppFieldType.url:
    case AppFieldType.email:
      return TextOperators;
    default:
      return [];
  }
};
